// Баннер согласия на cookie — один компонент на оба фронта
// (`sso.iec.exchange` и `id.iec.exchange`). Тексты приходят пропами: кит
// переводов не знает, i18n живёт в приложении.
//
// GDPR-требования, зашитые в саму форму, а не оставленные на усмотрение экрана:
//
//   1. **Отказаться не сложнее, чем согласиться.** «Отклонить всё» стоит на
//      первом же экране рядом с «Принять все» и той же величины. Отказ за два
//      клика при согласии за один — распространённый dark pattern и прямое
//      нарушение ст. 7(3).
//   2. **До выбора не ставится ничего необязательного.** Пока решения нет,
//      `readCookieConsent()` отдаёт `null`, а `isCookieCategoryGranted()` —
//      `false` для каждой настраиваемой категории. Показ баннера сам по себе
//      согласием не является.
//   3. **Баннер не блокирует чтение документов.** Ни скрима, ни перехвата
//      фокуса, ни `inert` на странице: он остаётся плашкой внизу, поверх
//      которой юридический текст читается целиком.
//   4. **Решение изменяемо.** Записанный выбор редактируется из витрины —
//      баннер не единственная точка входа.

import {
	allCookieChoices,
	COOKIE_CATEGORIES,
	COOKIE_CONSENT_STORAGE_KEY,
	type CookieCategoryChoices,
	type CookieCategoryKey,
	defaultCookieChoices,
	initialCookieChoices,
	isConfigurableCookieCategory,
	PREFERENCE_STORAGE_KEYS,
} from "@iec-id/shared/constants";
import {
	type ReactNode,
	useCallback,
	useEffect,
	useRef,
	useState,
	useSyncExternalStore,
} from "react";
import { cn } from "../lib/cn";
import { Button } from "./button";
import { Icon } from "./icon";
import { Switch } from "./switch";

// ─── хранилище гостевого выбора ─────────────────────────────────────────────

/**
 * Решение, сохранённое браузером. До входа записывать его больше некуда;
 * после входа источником истины становится реестр согласий на сервере, а эта
 * запись остаётся кэшем — по ней баннер решает, показываться ли снова.
 */
export type StoredCookieConsent = {
	readonly choices: CookieCategoryChoices;
	/** ISO-8601. */
	readonly decidedAt: string;
	/** Редакция документа `cookies`, которую видел человек (если успела загрузиться). */
	readonly version: string | null;
	readonly sha256: string | null;
	/**
	 * Выбор уже перенесён в реестр согласий. `false` — перенос ещё предстоит
	 * при ближайшем входе; поле и есть очередь на перенос.
	 */
	readonly synced: boolean;
};

function isStoredConsent(value: unknown): value is StoredCookieConsent {
	if (typeof value !== "object" || value === null) return false;
	const record = value as Record<string, unknown>;
	const choices = record.choices;
	if (typeof choices !== "object" || choices === null) return false;
	const map = choices as Record<string, unknown>;
	return (
		typeof map.preferences === "boolean" &&
		typeof map.analytics === "boolean" &&
		typeof record.decidedAt === "string"
	);
}

/** Подписчики на изменение выбора — в том числе из соседней вкладки. */
const listeners = new Set<() => void>();
let cached: { raw: string | null; value: StoredCookieConsent | null } = { raw: null, value: null };

function notify(): void {
	for (const listener of listeners) listener();
}

/** `null` — человек ещё не отвечал. Это НЕ «согласился на всё». */
export function readCookieConsent(): StoredCookieConsent | null {
	if (typeof localStorage === "undefined") return null;
	let raw: string | null;
	try {
		raw = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
	} catch {
		// Приватный режим / хранилище отключено: считаем, что выбора нет.
		return null;
	}
	// Кэш нужен не ради скорости: `useSyncExternalStore` требует стабильной
	// ссылки, иначе каждый рендер видит «новый» снимок и уходит в цикл.
	if (raw === cached.raw) return cached.value;

	let value: StoredCookieConsent | null = null;
	if (raw !== null) {
		try {
			const parsed: unknown = JSON.parse(raw);
			value = isStoredConsent(parsed) ? parsed : null;
		} catch {
			value = null;
		}
	}
	cached = { raw, value };
	return value;
}

export function writeCookieConsent(consent: StoredCookieConsent): void {
	if (typeof localStorage === "undefined") return;
	try {
		localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, JSON.stringify(consent));
	} catch {
		// Хранилище недоступно — баннер закроется на текущую сессию вкладки.
	}
	// Согласие «Предпочтения» отозвано — стираем то, что по нему хранилось.
	// Без этой строчки тумблер был бы декорацией: настройки остались бы в
	// браузере после отказа.
	if (!consent.choices.preferences) clearPreferenceStorage();
	notify();
}

/** Стереть браузерные настройки, которые живут по согласию «Предпочтения». */
export function clearPreferenceStorage(): void {
	if (typeof localStorage === "undefined") return;
	for (const key of PREFERENCE_STORAGE_KEYS) {
		try {
			localStorage.removeItem(key);
		} catch {
			// Нечего чистить — хранилище недоступно.
		}
	}
}

/** Подписка на решение: и собственные записи, и вкладка по соседству. */
function subscribe(listener: () => void): () => void {
	listeners.add(listener);
	const onStorage = (event: StorageEvent): void => {
		if (event.key === null || event.key === COOKIE_CONSENT_STORAGE_KEY) listener();
	};
	if (typeof window !== "undefined") window.addEventListener("storage", onStorage);
	return () => {
		listeners.delete(listener);
		if (typeof window !== "undefined") window.removeEventListener("storage", onStorage);
	};
}

/** Текущее решение браузера. `null` — решения нет. */
export function useCookieConsent(): StoredCookieConsent | null {
	return useSyncExternalStore(subscribe, readCookieConsent, () => null);
}

/**
 * Разрешена ли категория ПРЯМО СЕЙЧАС. Единственная функция, которой
 * позволено отвечать на этот вопрос: решения «нет» и «отказ» обязаны быть
 * неразличимы для вызывающего кода.
 */
export function isCookieCategoryGranted(category: keyof CookieCategoryChoices): boolean {
	const consent = readCookieConsent();
	if (!consent) return false;
	return consent.choices[category] === true;
}

/** Записать решение сервера поверх браузерного кэша (после входа). */
export function syncCookieConsentFromServer(input: {
	choices: CookieCategoryChoices;
	decidedAt: string;
	version: string | null;
	sha256: string | null;
}): void {
	writeCookieConsent({ ...input, synced: true });
}

/** Пометить гостевой выбор перенесённым — очередь на перенос пуста. */
export function markCookieConsentSynced(): void {
	const consent = readCookieConsent();
	if (!consent || consent.synced) return;
	writeCookieConsent({ ...consent, synced: true });
}

/**
 * Снимок решения, каким его отдаёт реестр согласий. Кит не знает про tRPC,
 * поэтому форма описана здесь минимально — ровно то, что нужно для сверки.
 */
export type ServerCookieConsent = {
	readonly decided: boolean;
	readonly choices: CookieCategoryChoices;
	readonly decidedAt: string | null;
	readonly version: string | null;
	readonly sha256: string | null;
};

export type CookieSyncDecision =
	/** Ничего делать не нужно: состояния совпадают либо ответа ещё нет. */
	| { readonly action: "none" }
	/** Гостевой выбор пора перенести в реестр. */
	| { readonly action: "import"; readonly choices: CookieCategoryChoices }
	/** В реестре есть решение — кэш браузера обязан ему следовать. */
	| { readonly action: "adopt-server" };

/**
 * Что делать с расхождением между браузером и реестром.
 *
 * Вынесено отдельной чистой функцией не ради красоты: этот выбор делают ОБА
 * фронта, и разъехавшиеся копии дали бы разные ответы на один вопрос — а
 * вопрос здесь юридический. Направление всегда одно: браузер → реестр при
 * переносе, реестр → браузер во всех остальных случаях. Обратного пути нет,
 * иначе очистка localStorage тихо отзывала бы записанное согласие.
 */
export function decideCookieSync(input: {
	readonly local: StoredCookieConsent | null;
	/** `undefined` — ответ реестра ещё не получен. */
	readonly server: ServerCookieConsent | undefined;
	/** Сессия есть. У гостя переносить некуда. */
	readonly authenticated: boolean;
}): CookieSyncDecision {
	const { local, server, authenticated } = input;
	if (!server) return { action: "none" };

	if (server.decided) {
		const same =
			local?.synced === true &&
			local.decidedAt === server.decidedAt &&
			local.version === server.version &&
			local.choices.preferences === server.choices.preferences &&
			local.choices.analytics === server.choices.analytics;
		return same ? { action: "none" } : { action: "adopt-server" };
	}

	if (!authenticated || local === null || local.synced) return { action: "none" };
	return { action: "import", choices: local.choices };
}

// ─── баннер ─────────────────────────────────────────────────────────────────

export type CookieCategoryLabels = {
	readonly label: string;
	readonly hint: string;
};

export type CookieBannerLabels = {
	readonly title: string;
	readonly description: string;
	readonly learnMore: string;
	readonly acceptAll: string;
	readonly rejectAll: string;
	readonly configure: string;
	readonly back: string;
	readonly save: string;
	readonly alwaysOn: string;
	readonly close: string;
	readonly categories: Readonly<Record<CookieCategoryKey, CookieCategoryLabels>>;
};

export interface CookieBannerProps {
	labels: CookieBannerLabels;
	/**
	 * Ссылка на публичный текст документа `cookies`. Открывается без сессии —
	 * иначе «подробнее» упирался бы в форму входа, а согласие переставало быть
	 * информированным.
	 */
	documentHref: string;
	/** Редакция документа рядом с баннером: уезжает в реестр вместе с выбором. */
	document?: { version: string; sha256: string } | undefined;
	/** Вызывается после записи решения — например, чтобы отправить его на сервер. */
	onDecision?: ((consent: StoredCookieConsent) => void) | undefined;
	/** Ссылка рендерится приложением: у витрины это роутер, у входа — обычный `<a>`. */
	renderLink?: ((props: { href: string; children: ReactNode }) => ReactNode) | undefined;
	className?: string;
}

/**
 * Плашка внизу экрана. Не диалог: диалог перехватил бы фокус и закрыл собой
 * страницу с юридическим текстом — а её баннер закрывать не вправе.
 */
export function CookieBanner({
	labels,
	documentHref,
	document: documentVersion,
	onDecision,
	renderLink,
	className,
}: CookieBannerProps) {
	const consent = useCookieConsent();
	const [configuring, setConfiguring] = useState(false);
	const [draft, setDraft] = useState<CookieCategoryChoices>(initialCookieChoices);

	// Решение из соседней вкладки закрывает баннер и здесь: два открытых
	// окна не должны спрашивать одно и то же дважды.
	useEffect(() => {
		if (consent) setConfiguring(false);
	}, [consent]);

	/**
	 * Баннер `fixed`, то есть он ВЫНУТ из потока и накрывает собой низ страницы.
	 * На телефоне это не косметика: на 390×844 плашка занимала нижние ~40 %
	 * экрана и полностью накрывала поле ввода почты на форме входа, а страница
	 * ровно в высоту окна — прокрутить из-под неё было нечего. Поле физически
	 * не нажималось, пока человек не ответит про cookie (проверено
	 * `elementFromPoint` по центру поля).
	 *
	 * Поэтому пока баннер виден, страница получает отступ снизу на его высоту —
	 * контент можно доскроллить, а не «доскроллить до плашки». Высота
	 * измеряется, а не задаётся числом: она зависит от локали (немецкий текст
	 * выше) и от того, раскрыты ли настройки категорий.
	 */
	const bannerRef = useRef<HTMLDivElement>(null);
	const visible = consent === null;
	useEffect(() => {
		const node = bannerRef.current;
		if (!visible || !node || typeof ResizeObserver === "undefined") return;

		const body = node.ownerDocument.body;
		const previous = body.style.paddingBottom;
		const apply = () => {
			body.style.paddingBottom = `${Math.ceil(node.getBoundingClientRect().height)}px`;
		};
		apply();
		const observer = new ResizeObserver(apply);
		observer.observe(node);
		return () => {
			observer.disconnect();
			body.style.paddingBottom = previous;
		};
	}, [visible]);

	const decide = useCallback(
		(choices: CookieCategoryChoices) => {
			const stored: StoredCookieConsent = {
				choices,
				decidedAt: new Date().toISOString(),
				version: documentVersion?.version ?? null,
				sha256: documentVersion?.sha256 ?? null,
				synced: false,
			};
			writeCookieConsent(stored);
			onDecision?.(stored);
		},
		[documentVersion, onDecision],
	);

	if (consent) return null;

	const link = renderLink ? (
		renderLink({ href: documentHref, children: labels.learnMore })
	) : (
		<a href={documentHref} className="underline underline-offset-2">
			{labels.learnMore}
		</a>
	);

	return (
		<div
			ref={bannerRef}
			// `z-110` — под диалогами (`z-120`) и над содержимым страницы:
			// модалка принятия документа обязана перекрывать баннер, а не наоборот.
			className={cn(
				"pointer-events-none fixed inset-x-0 bottom-0 z-110 flex justify-center",
				"px-1.5 pb-[max(0.375rem,env(safe-area-inset-bottom))] lg:px-2.5 lg:pb-2.5",
				className,
			)}
		>
			<section
				aria-label={labels.title}
				className={cn(
					"pointer-events-auto flex w-full max-w-[620px] flex-col gap-3.5",
					"rounded-card bg-card p-4 shadow-float md:p-5",
					"[animation:iec-reveal_var(--dur-500)_var(--ease-elegant)]",
				)}
			>
				<div className="flex items-start gap-2.5">
					<Icon name="Cookie" size={20} className="mt-0.5 shrink-0 text-brand-text" />
					<div className="flex min-w-0 flex-col gap-1">
						<h2 className="t-subsection">{labels.title}</h2>
						<p className="t-body text-fg-secondary">
							{labels.description} {link}
						</p>
					</div>
				</div>

				{configuring ? (
					<div className="flex flex-col gap-3.5">
						{COOKIE_CATEGORIES.map((category) => {
							const text = labels.categories[category.key];
							if (!isConfigurableCookieCategory(category.key)) {
								return (
									<Switch
										key={category.key}
										label={text.label}
										description={`${text.hint} · ${labels.alwaysOn}`}
										checked
										disabled
									/>
								);
							}
							const key = category.key;
							return (
								<Switch
									key={key}
									label={text.label}
									description={text.hint}
									checked={draft[key]}
									onCheckedChange={(enabled) =>
										setDraft((current) => ({ ...current, [key]: enabled }))
									}
								/>
							);
						})}
					</div>
				) : null}

				<div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
					{configuring ? (
						<>
							<Button
								variant="second"
								className="sm:min-w-[140px]"
								onClick={() => setConfiguring(false)}
							>
								{labels.back}
							</Button>
							<Button className="sm:min-w-[140px]" onClick={() => decide(draft)}>
								{labels.save}
							</Button>
						</>
					) : (
						<>
							<Button
								variant="second"
								className="sm:min-w-[140px]"
								onClick={() => {
									setDraft(initialCookieChoices());
									setConfiguring(true);
								}}
							>
								{labels.configure}
							</Button>
							{/*
							  «Отклонить всё» и «Принять все» — один вариант, один размер, один
							  ряд, один клик. Отказ не спрятан под «настроить», и он же не
							  проигрывает согласию визуально.

							  Зелёная заливка у «Принять все» была бы не стилем, а подсказкой:
							  бренд-цвет в системе означает основное действие, и на экране
							  СОГЛАСИЯ это ровно тот перекос, за который EDPB и CNIL штрафуют.
							  Требование «отказаться не сложнее, чем согласиться» — про
							  равенство обоих путей, а не только про число нажатий.
							*/}
							<Button
								variant="second"
								className="sm:min-w-[140px]"
								onClick={() => decide(defaultCookieChoices())}
							>
								{labels.rejectAll}
							</Button>
							<Button
								variant="second"
								className="sm:min-w-[140px]"
								onClick={() => decide(allCookieChoices())}
							>
								{labels.acceptAll}
							</Button>
						</>
					)}
				</div>
			</section>
		</div>
	);
}
